import React, { useState, useEffect } from 'react';
import { resizeAndCompressImage } from '../utils/imageUtils';
import {
  guardarTiposActividad,
  guardarVisitaOffline,
  obtenerTiposActividad,
  marcarClienteFinalizadoOffline,
  marcarClienteEstadoOffline,
} from '../db/indexedDB';
import { API_BASE, formatApiError, getStoredUser, getToken, isAuthError, notifySessionExpired } from '../services/api';

const categoriasFoto = [
  { id: 'doc_retiro', label: 'Documento Retiro' },
  { id: 'compro_pago', label: 'Comprobante de Pago' },
  { id: 'medi_visib', label: 'Medidor Visible' },
  { id: 'medi_visib2', label: 'Medidor Visible 2' },
  { id: 'ph', label: 'PH' },
  { id: 'fot_carta', label: 'Foto de Carta' },
  { id: 'verifi_lectura', label: 'Verificacion de Lectura' },
  { id: 'cond_empalme', label: 'Condicion de Empalme' },
];

const ACTIVIDADES_INVALIDAS = new Set(['', 'n/a', 'na', 'no informada', 'sin actividad']);

const actividadEsValida = (valor) => !ACTIVIDADES_INVALIDAS.has(String(valor || '').trim().toLowerCase());
const resultadosVisita = [
  { id: 'finalizada', label: 'Atendido y completado' },
  { id: 'sin_atencion', label: 'No atendieron' },
  { id: 'reprogramada', label: 'Reprogramado' },
  { id: 'direccion_no_encontrada', label: 'Problema con direccion' },
  { id: 'rechazo_cliente', label: 'Cliente rechazo atencion' },
];
const resultadosNoCierre = new Set(['sin_atencion', 'reprogramada', 'direccion_no_encontrada', 'rechazo_cliente']);
const ejemploMotivoPorResultado = {
  sin_atencion: 'Ej: Domicilio sin moradores, no responde llamado, nadie atiende.',
  reprogramada: 'Ej: Cliente solicita reprogramacion, no hay acceso, requiere nueva visita.',
  direccion_no_encontrada: 'Ej: Direccion incompleta, numeracion no encontrada, acceso no ubicado.',
  rechazo_cliente: 'Ej: Cliente rechaza atencion, no autoriza revision, no permite ingreso.',
};
const estadoClientePorResultado = (resultado) => {
  if (resultado === 'finalizada') return 'finalizado';
  return 'revision_admin';
};

const FichaCliente = ({ clienteSeleccionado, onVisitaFinalizada }) => {
  const [cliente, setCliente] = useState(clienteSeleccionado || null);
  const [visitaData, setVisitaData] = useState({
    lec_medidor: '',
    num_sello: '',
    tipo_actividad: '',
    obs_visita: '',
    nom_client: '',
    fono_client: '',
    mail_client: '',
    resultado_visita: 'finalizada',
    motivo_no_cierre: '',
    fecha_reprogramada: '',
  });

  const [fotos, setFotos] = useState({
    doc_retiro: null,
    compro_pago: null,
    medi_visib: null,
    medi_visib2: null,
    ph: null,
    fot_carta: null,
    verifi_lectura: null,
    cond_empalme: null,
  });

  const [loadingFotos, setLoadingFotos] = useState(false);
  const [enviandoVisita, setEnviandoVisita] = useState(false);
  const [tiposActividad, setTiposActividad] = useState([]);

  useEffect(() => {
    setCliente(clienteSeleccionado || null);
  }, [clienteSeleccionado]);

  useEffect(() => {
    let mounted = true;

    const cargarTiposActividad = async () => {
      const actividadesLocales = await obtenerTiposActividad().catch(() => []);
      if (mounted && actividadesLocales.length) {
        setTiposActividad(actividadesLocales);
      }

      try {
        const res = await fetch(`${API_BASE}/actividades/`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = res.ok ? await res.json() : [];
        const actividadesRemotas = Array.isArray(data) ? data : [];
        if (actividadesRemotas.length) {
          await guardarTiposActividad(actividadesRemotas);
          if (mounted) {
            setTiposActividad(actividadesRemotas);
          }
        }
      } catch {
        if (mounted && !actividadesLocales.length) {
          setTiposActividad([]);
        }
      }
    };

    void cargarTiposActividad();

    return () => {
      mounted = false;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVisitaData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumericInputChange = (e) => {
    const { name, value } = e.target;
    setVisitaData((prev) => ({ ...prev, [name]: value.replace(/\D/g, '') }));
  };

  const handlePhotoCapture = async (e, categoria) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoadingFotos(true);
    try {
      const watermarkInfo = {
        tecnicoNombre: getStoredUser()?.nombre || 'Técnico',
        codCliente: cliente?.cod_cliente,
        comuna: cliente?.comuna,
      };

      const { file: compressedFile, previewUrl, sizeKb } = await resizeAndCompressImage(file, watermarkInfo);

      setFotos((prev) => ({
        ...prev,
        [categoria]: {
          file: compressedFile,
          previewUrl,
          sizeKb,
        },
      }));
    } catch (error) {
      console.error('Error al comprimir la imagen:', error);
      alert('Error al procesar la fotografía. Intenta nuevamente.');
    } finally {
      setLoadingFotos(false);
    }
  };

  const handleRemovePhoto = (categoria) => {
    setFotos((prev) => ({ ...prev, [categoria]: null }));
  };

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFinalizarVisita = async (e) => {
    e.preventDefault();
    const resultadoVisita = visitaData.resultado_visita || 'finalizada';
    const esCierreFinal = resultadoVisita === 'finalizada';
    const requiereMotivo = resultadosNoCierre.has(resultadoVisita);

    if (!actividadEsValida(visitaData.tipo_actividad)) {
      alert('Debes seleccionar el Tipo de Actividad Realizada antes de guardar la visita.');
      return;
    }

    const correo = visitaData.mail_client.trim();
    if (esCierreFinal && correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      alert('Ingresa un correo electrónico válido o deja el campo vacío.');
      return;
    }

    if (requiereMotivo && !visitaData.motivo_no_cierre.trim()) {
      alert('Selecciona o ingresa el motivo de no cierre para este resultado.');
      return;
    }

    if (resultadoVisita === 'reprogramada' && !visitaData.fecha_reprogramada) {
      alert('Selecciona la fecha de reprogramacion.');
      return;
    }

    const totalFotos = Object.values(fotos).filter((foto) => foto !== null).length;
    if (esCierreFinal && totalFotos === 0) {
      alert('Debes tomar al menos una fotografía de evidencia antes de finalizar.');
      return;
    }

    const operationId = globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
          const random = Math.floor(Math.random() * 16);
          const value = char === 'x' ? random : ((random & 0x3) | 0x8);
          return value.toString(16);
        });

    setEnviandoVisita(true);
    try {
      const fotosPayload = {};
      for (const [tipo, foto] of Object.entries(fotos)) {
        if (!foto?.file) continue;
        fotosPayload[tipo] = {
          tipo,
          data_url: await fileToDataUrl(foto.file),
        };
      }

      const visitaPayload = {
        operation_id: operationId,
        cliente,
        visitaData,
        fotos: fotosPayload,
        nombre_tecnico: getStoredUser()?.nombre,
      };
      const finalizarComoOffline = async () => {
        await guardarVisitaOffline(visitaPayload);
        if (esCierreFinal) {
          await marcarClienteFinalizadoOffline(cliente);
        } else {
          await marcarClienteEstadoOffline(cliente, estadoClientePorResultado(resultadoVisita), { intento_offline: true });
        }
        onVisitaFinalizada?.({
          visita_id: null,
          pdf_url: null,
          cliente,
          visitaData,
          totalFotos,
          fecha: new Date().toISOString(),
          pendienteOffline: true,
          resultado_visita: resultadoVisita,
        });
      };

      if (!navigator.onLine) {
        await finalizarComoOffline();
        alert('Visita guardada sin señal. Se sincronizará automáticamente cuando vuelva la conexión.');
        return;
      }

      const res = await fetch(`${API_BASE}/visitas/completar`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(visitaPayload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        if (isAuthError(res, data)) notifySessionExpired();
        throw new Error(formatApiError(res, data, 'No se pudo guardar la visita.'));
      }
      onVisitaFinalizada?.({
        visita_id: data.visita_id,
        pdf_url: data.pdf_url,
        cliente,
        visitaData,
        totalFotos,
        fecha: new Date().toISOString(),
      });
    } catch (error) {
      if (!navigator.onLine || error instanceof TypeError) {
        try {
          const fotosPayload = {};
          for (const [tipo, foto] of Object.entries(fotos)) {
            if (!foto?.file) continue;
            fotosPayload[tipo] = {
              tipo,
              data_url: await fileToDataUrl(foto.file),
            };
          }
          await guardarVisitaOffline({
            operation_id: operationId,
            cliente,
            visitaData,
            fotos: fotosPayload,
            nombre_tecnico: getStoredUser()?.nombre,
          });
          if (esCierreFinal) {
            await marcarClienteFinalizadoOffline(cliente);
          } else {
            await marcarClienteEstadoOffline(cliente, estadoClientePorResultado(resultadoVisita), { intento_offline: true });
          }
          onVisitaFinalizada?.({
            visita_id: null,
            pdf_url: null,
            cliente,
            visitaData,
            totalFotos,
            fecha: new Date().toISOString(),
            pendienteOffline: true,
            resultado_visita: resultadoVisita,
          });
          alert('Se perdió la señal. La visita quedó guardada localmente y se subirá cuando vuelva la conexión.');
        } catch (offlineError) {
          alert(offlineError.message || 'No se pudo guardar la visita sin conexión.');
        }
      } else {
        alert(error.message);
      }
    } finally {
      setEnviandoVisita(false);
    }
  };

  if (!cliente) {
    return (
      <div className="gv-card" style={{ padding: '24px' }}>
        <p className="gv-section-title">Ficha de cliente</p>
        <p className="import-empty">No hay un cliente real seleccionado para completar visita.</p>
      </div>
    );
  }

  const resultadoActual = visitaData.resultado_visita || 'finalizada';
  const requiereMotivoActual = resultadosNoCierre.has(resultadoActual);
  const totalFotosActual = Object.values(fotos).filter(Boolean).length;
  const actividadCompleta = actividadEsValida(visitaData.tipo_actividad);
  const contactoCompleto = resultadoActual !== 'finalizada' || Boolean(visitaData.nom_client.trim());
  const evidenciaCompleta = resultadoActual !== 'finalizada' || totalFotosActual > 0;
  const requisitosCompletos = [actividadCompleta, contactoCompleto, evidenciaCompleta].filter(Boolean).length;

  return (
    <div className="visit-form">
      <section className="client-summary">
        <div className="client-summary__header">
          <div>
            <span className="client-summary__eyebrow">Ficha de cliente</span>
            <h3>{cliente.r_social}</h3>
            <p>{cliente.direccion}, {cliente.comuna}</p>
          </div>
          <div className="client-summary__gestion">
            <span>N° Gestión</span>
            <strong>{cliente.num_gestion}</strong>
          </div>
        </div>

        <div className="client-summary__grid">
          <div className="client-field">
            <span>Cod. Cliente</span>
            <strong>{cliente.cod_cliente}</strong>
          </div>
          <div className="client-field">
            <span>Comuna</span>
            <strong>{cliente.comuna}</strong>
          </div>
          <div className="client-field">
            <span>Medidor</span>
            <strong>{cliente.medidor}</strong>
          </div>
          <div className="client-field">
            <span>Marca medidor</span>
            <strong>{cliente.m_medidor}</strong>
          </div>
          <div className="client-field client-field--debt">
            <span>Deuda vencida</span>
            <strong>${Number(cliente.deuda_vencida).toLocaleString('es-CL')}</strong>
          </div>
        </div>
      </section>

      <h2>Ejecución de Visita</h2>
      <section className="visit-readiness" aria-label="Preparación del registro">
        <div>
          <span>Preparación del registro</span>
          <strong>{requisitosCompletos} de 3 requisitos listos</strong>
        </div>
        <ul>
          <li className={actividadCompleta ? 'complete' : ''}>Actividad</li>
          <li className={contactoCompleto ? 'complete' : ''}>Persona atendida</li>
          <li className={evidenciaCompleta ? 'complete' : ''}>Evidencia</li>
        </ul>
      </section>
      <form noValidate onSubmit={handleFinalizarVisita} className="visit-form__body">
        <section className="visit-section">
          <h4>Datos Operacionales</h4>
          <div className="visit-grid">
            <div className="gv-form-group">
              <label className="gv-label">Lectura medidor</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="lec_medidor"
                value={visitaData.lec_medidor}
                onChange={handleNumericInputChange}
                className="gv-input"
              />
              <small className="gv-field-help">Si no puedes acceder a este dato, marca 0.</small>
            </div>
            <div className="gv-form-group">
              <label className="gv-label">Número de sello</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="num_sello"
                value={visitaData.num_sello}
                onChange={handleNumericInputChange}
                className="gv-input"
              />
              <small className="gv-field-help">Si no puedes acceder a este dato, marca 0.</small>
            </div>
            <div className="gv-form-group visit-grid__full">
              <label className="gv-label">Tipo de Actividad Realizada *</label>
              <select
                name="tipo_actividad"
                value={visitaData.tipo_actividad}
                onChange={handleInputChange}
                className="gv-select"
                required
                aria-required="true"
              >
                <option value="">Seleccione una actividad...</option>
                {tiposActividad.map((actividad) => (
                  <option key={actividad.id} value={actividad.nombre}>
                    {actividad.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="gv-form-group visit-grid__full">
              <label className="gv-label">Observaciones de la Visita</label>
              <textarea
                name="obs_visita"
                value={visitaData.obs_visita}
                onChange={handleInputChange}
                rows="3"
                className="gv-textarea"
              />
            </div>
          </div>
        </section>

        <section className="visit-section">
          <h4>Atendido por</h4>
          <div className="visit-grid">
            <div className="gv-form-group visit-grid__full">
              <label className="gv-label">Nombre de quien recibe</label>
              <input type="text" name="nom_client" value={visitaData.nom_client} onChange={handleInputChange} className="gv-input" required={resultadoActual === 'finalizada'} />
            </div>
            <div className="gv-form-group">
              <label className="gv-label">Teléfono contacto <span className="gv-label__optional">(opcional)</span></label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="fono_client"
                value={visitaData.fono_client}
                onChange={handleNumericInputChange}
                className="gv-input"
              />
            </div>
            <div className="gv-form-group">
              <label className="gv-label">Correo electrónico <span className="gv-label__optional">(opcional)</span></label>
              <input type="email" name="mail_client" value={visitaData.mail_client} onChange={handleInputChange} className="gv-input" />
            </div>
          </div>
        </section>

        <section className="visit-section visit-section--photos">
          <div className="visit-section__heading">
            <div>
              <h4>Evidencias fotográficas</h4>
              <p>Toma o sube fotos. Se comprimirán automáticamente a máximo 1200 px para ahorrar datos.</p>
            </div>
            <span className={`photo-count ${totalFotosActual ? 'complete' : ''}`}>
              {totalFotosActual} de {categoriasFoto.length}
            </span>
          </div>
          {loadingFotos && <p className="visit-processing">Procesando y comprimiendo imagen...</p>}

          <div className="photo-grid">
            {categoriasFoto.map((categoria) => (
              <div key={categoria.id} className="photo-tile">
                <strong>{categoria.label}</strong>
                {!fotos[categoria.id] ? (
                  <>
                    <label htmlFor={`camera-${categoria.id}`} className="photo-upload">
                      Tomar foto
                    </label>
                    <input
                      id={`camera-${categoria.id}`}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoCapture(e, categoria.id)}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor={`gallery-${categoria.id}`} className="photo-upload">
                      Elegir de galeria
                    </label>
                    <input
                      id={`gallery-${categoria.id}`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoCapture(e, categoria.id)}
                      style={{ display: 'none' }}
                    />
                  </>
                ) : (
                  <div>
                    <img src={fotos[categoria.id].previewUrl} alt={categoria.label} className="photo-preview" />
                    <p className="photo-size">~{Math.round(fotos[categoria.id].sizeKb)} kb</p>
                    <button type="button" onClick={() => handleRemovePhoto(categoria.id)} className="photo-remove">
                      Borrar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="visit-section visit-section--result">
          <div className="visit-result-heading">
            <h4>Resultado de la visita</h4>
          </div>
          <div className="visit-grid">
            <div className="gv-form-group visit-grid__full">
              <label className="gv-label">Estado de la atencion</label>
              <select
                name="resultado_visita"
                value={visitaData.resultado_visita}
                onChange={handleInputChange}
                className="gv-select"
                required
              >
                {resultadosVisita.map((resultado) => (
                  <option key={resultado.id} value={resultado.id}>{resultado.label}</option>
                ))}
              </select>
            </div>
            {requiereMotivoActual && (
              <div className="gv-form-group visit-grid__full">
                <label className="gv-label">Motivo no cierre *</label>
                <textarea
                  name="motivo_no_cierre"
                  value={visitaData.motivo_no_cierre}
                  onChange={handleInputChange}
                  rows="2"
                  className="gv-textarea"
                  placeholder={ejemploMotivoPorResultado[resultadoActual] || 'Ingresa el motivo de no cierre.'}
                  required
                />
              </div>
            )}
            {resultadoActual === 'reprogramada' && (
              <div className="gv-form-group">
                <label className="gv-label">Fecha reprogramada *</label>
                <input
                  type="date"
                  name="fecha_reprogramada"
                  value={visitaData.fecha_reprogramada}
                  onChange={handleInputChange}
                  className="gv-input"
                  required
                />
              </div>
            )}
          </div>
        </section>

        <div className="visit-submit-summary" role="status">
          <div>
            <strong>{resultadoActual === 'finalizada' ? 'Cierre con respaldo PDF' : 'Registro de intento'}</strong>
            <span>
              {resultadoActual === 'finalizada'
                ? `${totalFotosActual} evidencia(s) preparada(s). Revisa la actividad y la persona atendida antes de guardar.`
                : 'El caso quedará disponible para revisión administrativa.'}
            </span>
          </div>
          <span className={requisitosCompletos === 3 ? 'ready' : ''}>
            {requisitosCompletos === 3 ? 'Listo para guardar' : 'Faltan datos obligatorios'}
          </span>
        </div>
        <button type="submit" className="gv-btn gv-btn--primary gv-btn--full gv-btn--lg" disabled={enviandoVisita}>
          {enviandoVisita ? 'Guardando visita...' : (resultadoActual === 'finalizada' ? 'Guardar visita y generar PDF' : 'Registrar intento y generar PDF')}
        </button>
      </form>
    </div>
  );
};

export default FichaCliente;
